import React, { useEffect } from 'react';
import {
  Drawer,
  Form,
  Input,
  Button,
  Space,
  Row,
  Col,
  Select,
  Switch
} from 'antd';
import { User } from '../../services/userService';

const { Option } = Select;

interface UserModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (data: any) => void;
  editingUser: User | null;
  loading?: boolean;
}

const UserModal: React.FC<UserModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  editingUser,
  loading = false
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      if (editingUser) {
        form.setFieldsValue({
          username: editingUser.username,
          email: editingUser.email,
          full_name: editingUser.full_name,
          role: editingUser.role,
          is_active: editingUser.is_active,
          status: editingUser.status || 'active'
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          role: 'assistant',
          is_active: true,
          status: 'active'
        });
      }
    }
  }, [visible, editingUser, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onSubmit(values);
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  return (
    <Drawer
      title={editingUser ? 'Sửa người dùng' : 'Thêm người dùng mới'}
      open={visible}
      onClose={onCancel}
      width={600}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onCancel}>
            Hủy
          </Button>
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            {editingUser ? 'Cập nhật' : 'Tạo người dùng'}
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        {!editingUser && (
          <Form.Item
            name="username"
            label="Tên đăng nhập"
            rules={[
              { required: true, message: 'Vui lòng nhập tên đăng nhập' },
              { min: 3, message: 'Tên đăng nhập phải có ít nhất 3 ký tự' },
              { max: 50, message: 'Tên đăng nhập không được quá 50 ký tự' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: 'Tên đăng nhập chỉ được chứa chữ cái, số và dấu gạch dưới' }
            ]}
          >
            <Input placeholder="Nhập tên đăng nhập" />
          </Form.Item>
        )}

        {!editingUser && (
          <Form.Item
            name="password"
            label="Mật khẩu"
            rules={[
              { required: true, message: 'Vui lòng nhập mật khẩu' },
              { min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự' }
            ]}
          >
            <Input.Password placeholder="Nhập mật khẩu" />
          </Form.Item>
        )}

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="full_name"
              label="Họ và tên"
              rules={[
                { required: true, message: 'Vui lòng nhập họ và tên' },
                { max: 100, message: 'Họ và tên không được quá 100 ký tự' }
              ]}
            >
              <Input placeholder="Nhập họ và tên" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Vui lòng nhập email' },
                { type: 'email', message: 'Email không hợp lệ' }
              ]}
            >
              <Input placeholder="Nhập email" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="role"
              label="Vai trò"
              rules={[
                { required: true, message: 'Vui lòng chọn vai trò' }
              ]}
            >
              <Select placeholder="Chọn vai trò">
                <Option value="admin">Quản trị viên</Option>
                <Option value="pharmacist">Dược sĩ</Option>
                <Option value="assistant">Trợ lý</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="status"
              label="Trạng thái"
              rules={[
                { required: true, message: 'Vui lòng chọn trạng thái' }
              ]}
            >
              <Select placeholder="Chọn trạng thái">
                <Option value="active">Đã kích hoạt</Option>
                <Option value="pending">Chờ duyệt</Option>
                <Option value="inactive">Vô hiệu</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {editingUser && (
          <Form.Item
            name="is_active"
            label="Trạng thái hoạt động"
            valuePropName="checked"
          >
            <Switch checkedChildren="Hoạt động" unCheckedChildren="Không hoạt động" />
          </Form.Item>
        )}
      </Form>
    </Drawer>
  );
};

export default UserModal;

